import { useCallback, useEffect, useState } from "react";
import { fetchWalletDetail } from "@/lib/api";
import type { WalletDetail } from "@/types";

interface UseWalletDetailResult {
  detail: WalletDetail | null;
  loading: boolean;
  error: string | null;
  source: "api" | "mock";
  refresh: () => void;
}

function mapApiWalletDetail(raw: Record<string, unknown>): WalletDetail {
  // GatewayWalletDetailResponse wraps wallet fields inside a nested "wallet" object.
  // Unwrap it so field reads (walletId, chain, …) resolve correctly.
  const w = (raw.wallet && typeof raw.wallet === "object" && !Array.isArray(raw.wallet))
    ? raw.wallet as Record<string, unknown>
    : raw;

  const id = String(w.walletId ?? w.id ?? "");
  const name = String(w.name ?? w.accountAlias ?? w.label ?? id);
  const chain = String(w.chain ?? "—");
  const network = String(w.network ?? "—");
  const address = String(w.address ?? "—");
  const walletProfileId = String(w.walletProfileId ?? "—");
  const environment = String(w.environment ?? "sandbox");
  const createdAt = w.createdAt ? new Date(String(w.createdAt)).toLocaleString() : "—";

  const stats = Array.isArray(raw.stats)
    ? (raw.stats as Record<string, unknown>[]).map(s => ({
        value: String(s.value ?? ""),
        label: String(s.label ?? ""),
        hint: typeof s.hint === "string" ? s.hint : undefined,
        positiveHint: s.positiveHint === true,
        tone: typeof s.tone === "string" ? s.tone as "default" | "success" : undefined,
      }))
    : [
        { value: "$0", label: "Total Balance", hint: environment === "sandbox" ? "Sandbox wallet" : null },
        { value: chain, label: "Chain" },
        { value: network, label: "Network" },
        { value: environment.charAt(0).toUpperCase() + environment.slice(1), label: "Environment", tone: "success" as const },
      ];

  // GatewayBalanceSnapshot uses "asset" / "balance"; map to UI field names.
  const balances = Array.isArray(raw.balances)
    ? (raw.balances as Record<string, unknown>[]).map(b => ({
        symbol: String(b.symbol ?? b.asset ?? ""),
        name: String(b.name ?? b.symbol ?? b.asset ?? ""),
        amount: String(b.amount ?? b.balance ?? "0"),
        usd: String(b.usd ?? "$0"),
        iconLabel: String(b.iconLabel ?? b.symbol ?? b.asset ?? ""),
      }))
    : chain === "sandbox-ledger"
      ? [{ symbol: "SBX", name: "Sandbox Token", amount: "0 SBX", usd: "$0", iconLabel: "SB" }]
      : [];

  const routes = Array.isArray(raw.routes)
    ? (raw.routes as Record<string, unknown>[]).map(r => ({
        title: String(r.title ?? ""),
        supported: r.supported === true,
        detail: String(r.detail ?? ""),
      }))
    : [
        { title: "Receive", supported: true, detail: "Deposit to wallet address" },
        { title: "Send", supported: walletProfileId.includes("signing") || walletProfileId.includes("fireblocks"), detail: walletProfileId.includes("fireblocks") ? "MPC signing via Fireblocks" : walletProfileId.includes("signing") ? "Sign & broadcast via Mesh" : "Sandbox — no real signing" },
        { title: "Swap", supported: false, detail: "Not available in sandbox" },
        { title: "Stake", supported: false, detail: "Not available in sandbox" },
      ];

  const recentTransfers = Array.isArray(raw.recentTransfers)
    ? (raw.recentTransfers as Record<string, unknown>[]).map(t => ({
        id: String(t.id ?? ""),
        direction: String(t.direction ?? "sent") as "sent" | "received",
        asset: String(t.asset ?? ""),
        amount: String(t.amount ?? "0"),
        status: String(t.status ?? ""),
        statusVariant: String(t.statusVariant ?? "neutral") as "success" | "warning" | "danger" | "pending" | "neutral" | "info",
        time: String(t.time ?? ""),
      }))
    : [];

  const properties = Array.isArray(raw.properties)
    ? (raw.properties as Record<string, unknown>[]).map(p => ({ k: String(p.k ?? p.key ?? ""), v: String(p.v ?? p.value ?? "") }))
    : [
        { k: "Wallet ID", v: id },
        { k: "Address", v: address },
        { k: "Chain", v: chain },
        { k: "Network", v: network },
        { k: "Provider", v: walletProfileId.includes("fireblocks") ? "Fireblocks" : walletProfileId.includes("signing") ? "Mesh (VaultCore)" : "Sandbox" },
        { k: "Wallet Profile", v: walletProfileId },
        { k: "Environment", v: environment },
        { k: "Created", v: createdAt },
      ];

  const evidence = Array.isArray(raw.evidence)
    ? (raw.evidence as Record<string, unknown>[]).map(e => ({ id: String(e.id ?? ""), label: String(e.label ?? e.id ?? "") }))
    : [{ id: "ev-creation", label: "Wallet creation audit record" }];

  return {
    id, name, stats, balances, routes, recentTransfers,
    properties, evidence,
    hasRecovery: raw.hasRecovery === true,
  };
}

export function useWalletDetail(id: string): UseWalletDetailResult {
  const [detail, setDetail] = useState<WalletDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<"api" | "mock">("api");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWalletDetail(id);
      if (result.wallet) {
        setDetail(mapApiWalletDetail(result.wallet));
        setSource("api");
      } else {
        setError(result.reasonCode || "Wallet not found");
        setDetail(null);
      }
    } catch {
      setError("Failed to load wallet details");
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  return { detail, loading, error, source, refresh: load };
}
